import { useState, useMemo } from 'react'
import { performanceService } from '../../performance/services/performanceService'
import type { PerformanceColaboradorLinha } from '../../performance/tipos/performance'
import {
  HoverTooltipNomeColaborador,
  HoverTooltipIntegrantesTime,
} from '../../../componentesCompartilhados/HoverTooltipHorasPerformance'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { Award, RefreshCw, Search, CheckCircle, AlertTriangle } from 'lucide-react'
import { formatCurrency, formatNumber } from '../../../utils'
import {
  usePremiacao,
  usePremiacaoDetalhe,
  usePremiacaoConferencias,
  obterPeriodoQuinzenaAnterior,
} from '../hooks/usePremiacao'
import { premiacaoService } from '../services/premiacaoService'
import { timeService } from '../../times/services/time'

const MESES = [
  { v: 1, l: 'Janeiro' },
  { v: 2, l: 'Fevereiro' },
  { v: 3, l: 'Março' },
  { v: 4, l: 'Abril' },
  { v: 5, l: 'Maio' },
  { v: 6, l: 'Junho' },
  { v: 7, l: 'Julho' },
  { v: 8, l: 'Agosto' },
  { v: 9, l: 'Setembro' },
  { v: 10, l: 'Outubro' },
  { v: 11, l: 'Novembro' },
  { v: 12, l: 'Dezembro' },
]

function anosSelect(): number[] {
  const y = new Date().getFullYear()
  return [y, y - 1, y - 2, y - 3, y - 4]
}

/** Status conferência: 0 Pendente, 1 Conferido, 2 Divergência */
const STATUS = { PENDENTE: 0, CONFERIDO: 1, DIVERGENCIA: 2 } as const

export default function PremiacaoTime() {
  const defaults = obterPeriodoQuinzenaAnterior()
  const [ano, setAno] = useState(defaults.ano)
  const [mes, setMes] = useState(defaults.mes)
  const [numeroQuinzena, setNumeroQuinzena] = useState(defaults.quinzena)
  const [filtroAtivo, setFiltroAtivo] = useState({ ano: defaults.ano, mes: defaults.mes, quinzena: defaults.quinzena })
  const [modalDivergencia, setModalDivergencia] = useState(false)
  const [textoDivergencia, setTextoDivergencia] = useState('')
  const queryClient = useQueryClient()

  const { data: resultado, isLoading: loadingPremiacao, refetch } = usePremiacao(
    filtroAtivo.ano,
    filtroAtivo.mes,
    filtroAtivo.quinzena,
  )
  const meuTime = resultado?.times?.[0]
  const { data: detalhe, isLoading: loadingDetalhe } = usePremiacaoDetalhe(
    filtroAtivo.ano,
    filtroAtivo.mes,
    filtroAtivo.quinzena,
    meuTime?.timeId,
  )
  const { data: conferencias = [] } = usePremiacaoConferencias(
    filtroAtivo.ano,
    filtroAtivo.mes,
    filtroAtivo.quinzena,
  )
  const minhaConf = conferencias[0]

  const { data: timesConfig = [] } = useQuery(
    'times-configuracao',
    () => timeService.listarTimesConfiguracao(),
    { staleTime: 5 * 60 * 1000 },
  )

  const integrantesMeuTime = useMemo(() => {
    if (!meuTime) return undefined
    const t = timesConfig.find((x) => x.id === meuTime.timeId)
    if (!t?.integrantes?.length) return undefined
    const nomes = t.integrantes.map((i) => i.nome).filter(Boolean)
    return nomes.length ? nomes.join(', ') : undefined
  }, [timesConfig, meuTime])

  const { data: performanceResumo } = useQuery(
    ['performance', 'premiacao-time', resultado?.dataInicio, resultado?.dataFim, meuTime?.timeId],
    () => performanceService.obter(resultado!.dataInicio, resultado!.dataFim, meuTime!.timeId),
    {
      enabled: !!resultado?.dataInicio && !!resultado?.dataFim && !!meuTime?.timeId,
      staleTime: 2 * 60 * 1000,
    },
  )

  const performancePorPessoa = useMemo(() => {
    const m = new Map<number, PerformanceColaboradorLinha>()
    for (const c of performanceResumo?.colaboradores ?? [])
      m.set(c.pessoaId, c)
    return m
  }, [performanceResumo])

  const mutConferencia = useMutation(
    (payload: { status: number; textoDivergencia?: string | null }) =>
      premiacaoService.definirConferencia({
        ano: filtroAtivo.ano,
        mes: filtroAtivo.mes,
        numeroQuinzena: filtroAtivo.quinzena,
        timeId: meuTime!.timeId,
        status: payload.status,
        textoDivergencia: payload.textoDivergencia,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['premiacao-conferencias'])
        queryClient.invalidateQueries(['premiacao-historico'])
        setModalDivergencia(false)
        setTextoDivergencia('')
      },
    },
  )

  const handleBuscar = () => {
    setFiltroAtivo({ ano, mes, quinzena: numeroQuinzena })
  }

  const enviarDivergencia = () => {
    if (!meuTime || !textoDivergencia.trim()) return
    mutConferencia.mutate({ status: STATUS.DIVERGENCIA, textoDivergencia: textoDivergencia.trim() })
  }

  const conferir = () => {
    if (!meuTime) return
    mutConferencia.mutate({ status: STATUS.CONFERIDO, textoDivergencia: null })
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto px-4">
      <div className="flex items-center gap-3">
        <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-gradient-to-br from-brand-500 to-brand-600">
          <Award className="w-5 h-5 text-white" />
        </div>
        <h1 className="text-2xl font-bold text-gray-900">Premiação do Meu Time</h1>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-theme-sm p-4">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Mês</label>
            <select
              value={mes}
              onChange={(e) => setMes(Number(e.target.value))}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 min-w-[140px]"
            >
              {MESES.map((m) => (
                <option key={m.v} value={m.v}>
                  {m.l}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Ano</label>
            <select
              value={ano}
              onChange={(e) => setAno(Number(e.target.value))}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700"
            >
              {anosSelect().map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </select>
          </div>
          <div>
            <span className="block text-xs font-medium text-gray-600 mb-1">Quinzena</span>
            <div className="flex gap-4 items-center pt-1">
              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                <input
                  type="radio"
                  name="qnz-t"
                  checked={numeroQuinzena === 1}
                  onChange={() => setNumeroQuinzena(1)}
                />
                1ª (1–15)
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                <input
                  type="radio"
                  name="qnz-t"
                  checked={numeroQuinzena === 2}
                  onChange={() => setNumeroQuinzena(2)}
                />
                2ª (16–fim)
              </label>
            </div>
          </div>
          <button
            onClick={handleBuscar}
            disabled={loadingPremiacao}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-brand-500 rounded-lg hover:bg-brand-600 disabled:opacity-50"
          >
            <Search className="w-4 h-4" />
            Buscar
          </button>
          <button
            onClick={() => refetch()}
            disabled={loadingPremiacao}
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loadingPremiacao ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {loadingPremiacao ? (
        <div className="flex items-center justify-center h-32">
          <RefreshCw className="w-6 h-6 animate-spin text-brand-500" />
        </div>
      ) : meuTime ? (
        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-gray-200 shadow-theme-sm p-6">
            <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
              <h2 className="text-lg font-semibold text-gray-900">
                {integrantesMeuTime ? (
                  <HoverTooltipIntegrantesTime titulo={meuTime.nomeTime} textoIntegrantes={integrantesMeuTime}>
                    <span>{meuTime.nomeTime}</span>
                  </HoverTooltipIntegrantesTime>
                ) : (
                  meuTime.nomeTime
                )}
              </h2>
              <div className="flex flex-wrap gap-2 items-center">
                <span className="text-xs text-gray-500">Conferência:</span>
                {minhaConf?.statusCodigo === STATUS.CONFERIDO && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium bg-green-100 text-green-800">
                    <CheckCircle className="w-3.5 h-3.5" />
                    Conferido
                  </span>
                )}
                {minhaConf?.statusCodigo === STATUS.DIVERGENCIA && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium bg-red-100 text-red-800">
                    <AlertTriangle className="w-3.5 h-3.5" />
                    Divergência registrada
                  </span>
                )}
                {(!minhaConf || minhaConf.statusCodigo === STATUS.PENDENTE) && (
                  <span className="inline-flex px-2 py-1 rounded-lg text-xs font-medium bg-gray-100 text-gray-700">
                    Pendente
                  </span>
                )}
              </div>
            </div>
            {minhaConf?.textoDivergencia ? (
              <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-100 text-sm text-red-900">
                <p className="font-medium text-xs text-red-700 mb-1">Texto da divergência</p>
                {minhaConf.textoDivergencia}
              </div>
            ) : null}

            <div className="flex flex-wrap gap-2 mb-6">
              <button
                type="button"
                onClick={conferir}
                disabled={mutConferencia.isLoading || !meuTime}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                <CheckCircle className="w-4 h-4" />
                Conferir
              </button>
              <button
                type="button"
                onClick={() => setModalDivergencia(true)}
                disabled={mutConferencia.isLoading || !meuTime}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 disabled:opacity-50"
              >
                <AlertTriangle className="w-4 h-4" />
                Registrar divergência
              </button>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <MetricaCard label="Membros" valor={String(meuTime.quantidadePessoas)} />
              <MetricaCard label="Horas Faturadas" valor={`${formatNumber(meuTime.horasFaturadas)}h`} />
              <MetricaCard label="Valor Faturado" valor={formatCurrency(meuTime.totalValorFaturado)} />
              <MetricaCard label="% Alcançado" valor={`${(meuTime.percentualAlcancado * 100).toFixed(2)}%`} />
            </div>
            <div className="mt-4 pt-4 border-t border-gray-200 grid grid-cols-2 gap-4">
              <div className="bg-green-50 rounded-lg p-4 text-center">
                <p className="text-sm text-green-600 font-medium">Premiação Total</p>
                <p className="text-2xl font-bold text-green-700">{formatCurrency(meuTime.premiacaoTotal)}</p>
              </div>
              <div className="bg-green-50 rounded-lg p-4 text-center">
                <p className="text-sm text-green-600 font-medium">Premiação por Pessoa</p>
                <p className="text-2xl font-bold text-green-700">{formatCurrency(meuTime.premiacaoPorPessoa)}</p>
              </div>
            </div>
          </div>

          {loadingDetalhe ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-5 h-5 animate-spin text-brand-500" />
            </div>
          ) : detalhe ? (
            <>
              <div className="bg-white rounded-xl border border-gray-200 shadow-theme-sm overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                  <h3 className="text-sm font-semibold text-gray-700">Faturamentos Enviados</h3>
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="px-4 py-2 text-left font-medium text-gray-600 text-xs">NF</th>
                      <th className="px-4 py-2 text-left font-medium text-gray-600 text-xs">Data Envio</th>
                      <th className="px-4 py-2 text-left font-medium text-gray-600 text-xs">Etapa</th>
                      <th className="px-4 py-2 text-left font-medium text-gray-600 text-xs">Projeto</th>
                      <th className="px-4 py-2 text-right font-medium text-gray-600 text-xs">Minutos</th>
                      <th className="px-4 py-2 text-right font-medium text-gray-600 text-xs">Valor</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detalhe.faturamentos.map((f) => (
                      <tr key={f.idFaturamento} className="border-b border-gray-100">
                        <td className="px-4 py-2 text-gray-700 text-xs">{f.nf || '-'}</td>
                        <td className="px-4 py-2 text-gray-700 text-xs">
                          {f.dataEnvioAnalista ? new Date(f.dataEnvioAnalista).toLocaleDateString('pt-BR') : '-'}
                        </td>
                        <td className="px-4 py-2 text-gray-700 text-xs">{f.etapaDescricao}</td>
                        <td className="px-4 py-2 text-gray-700 text-xs">{f.projetoNome}</td>
                        <td className="px-4 py-2 text-right text-gray-700 text-xs">{formatNumber(f.totalMinutos)}</td>
                        <td className="px-4 py-2 text-right text-gray-700 text-xs">{formatCurrency(f.valorTotal)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="bg-white rounded-xl border border-gray-200 shadow-theme-sm overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                  <h3 className="text-sm font-semibold text-gray-700">Colaboradores</h3>
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="px-4 py-2 text-left font-medium text-gray-600 text-xs">Nome</th>
                      <th className="px-4 py-2 text-right font-medium text-gray-600 text-xs">Minutos</th>
                      <th className="px-4 py-2 text-right font-medium text-gray-600 text-xs">Horas</th>
                      <th className="px-4 py-2 text-right font-medium text-gray-600 text-xs">Valor Faturado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detalhe.colaboradores.map((c) => (
                      <tr key={c.pessoaId} className="border-b border-gray-100">
                        <td className="px-4 py-2 font-medium text-gray-900 text-xs">
                          <HoverTooltipNomeColaborador
                            pessoaId={c.pessoaId}
                            nomeExibicao={c.nome}
                            performancePorPessoa={performancePorPessoa}
                          />
                        </td>
                        <td className="px-4 py-2 text-right text-gray-700 text-xs">{formatNumber(c.totalMinutos)}</td>
                        <td className="px-4 py-2 text-right text-gray-700 text-xs">{formatNumber(c.horas)}h</td>
                        <td className="px-4 py-2 text-right text-gray-700 text-xs">{formatCurrency(c.valorFaturado)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : null}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 shadow-theme-sm p-8 text-center text-gray-500">
          Nenhuma premiação encontrada para seu time neste período.
        </div>
      )}

      {modalDivergencia && meuTime && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Registrar divergência</h3>
            <p className="text-sm text-gray-600 mb-4">
              Descreva a divergência encontrada. O administrador será informado ao salvar.
            </p>
            <textarea
              value={textoDivergencia}
              onChange={(e) => setTextoDivergencia(e.target.value)}
              rows={5}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800"
              placeholder="Detalhe a divergência..."
            />
            <div className="flex justify-end gap-2 mt-4">
              <button
                type="button"
                onClick={() => {
                  setModalDivergencia(false)
                  setTextoDivergencia('')
                }}
                className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={enviarDivergencia}
                disabled={!textoDivergencia.trim() || mutConferencia.isLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                Enviar divergência
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function MetricaCard({ label, valor }: { label: string; valor: string }) {
  return (
    <div className="bg-gray-50 rounded-lg p-3 text-center">
      <p className="text-xs text-gray-500 mb-1">{label}</p>
      <p className="text-sm font-semibold text-gray-900">{valor}</p>
    </div>
  )
}
